import { Component, OnDestroy, Renderer2, Inject, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatorService } from './services/translator-service/translator.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from './services/loading-service/loading.service';
import { delay, Subscription, firstValueFrom, timeout } from 'rxjs';
import {
  EnvironmentConfigService,
  ILocalAppEnvironment,
} from './services/environment-config-service/environment-config-service';
import { App } from '@capacitor/app';
import { Platform, AlertController } from '@ionic/angular';
import { SafeAreaService } from './services/safe-area-service/safe-area-service';
import { PractitionerService } from './services/practitioner-service/practitioner.service';
import { IdleTimeoutService } from './services/idle-timeout/idle-timeout-service';
import { addIcons } from 'ionicons';
import {
  expand,
  accessibilityOutline,
  add,
  addCircleOutline,
  addOutline,
  alertCircleOutline,
  appsOutline,
  arrowBack,
  arrowBackOutline,
  arrowDownOutline,
  arrowForwardOutline,
  arrowUndoOutline,
  arrowUpOutline,
  bluetooth,
  bodyOutline,
  calendarNumberOutline,
  cameraOutline,
  checkmarkCircleOutline,
  checkmarkOutline,
  chevronBack,
  chevronDown,
  chevronForward,
  chevronForwardOutline,
  chevronUp,
  close,
  closeCircleOutline,
  code,
  cogOutline,
  colorWand,
  copyOutline,
  documentOutline,
  eyeOffOutline,
  eyeOutline,
  flashOutline,
  globeOutline,
  gridOutline,
  keyOutline,
  linkOutline,
  logOutOutline,
  mailOutline,
  notificationsOutline,
  pencilOutline,
  peopleOutline,
  person,
  personAddOutline,
  personCircleOutline,
  personOutline,
  pricetag,
  refreshOutline,
  reorderThreeOutline,
  returnDownBackOutline,
  saveOutline,
  settingsOutline,
  star,
  text,
  textOutline,
  trashOutline,
  unlinkSharp,
  warningOutline,
  document,
  library,
  folder,
  documents,
  contract,
  hammerOutline,
  homeOutline,
} from 'ionicons/icons';
import { SessionService } from './services/session-service/session-service';
import { NetworkService, StoragePrefixType } from './services/network-service/network-service';
import { PromptService } from './services/promtp-service/prompt.service';
import { AuthService } from './services/auth-service/auth.service';
import { OfflineSyncService, SyncTask } from './services/offline-sync-service/offline-sync-service';
import { EncounterCompleteEncounterPost$Params } from './api/proxy/practice/fn/encounter/encounter-complete-encounter-post';
import { TreatmentService } from './services/treatment-service/treatment.service';
import { Events } from './services/events/events';
import { SecureStorageService } from './services/secure-storage/secure-storage-service';
import { StorageKey } from './services/storage/models/storage-key.enum';
import { AppVersionGuardService } from './services/app-version-guard/app-version-guard.service';
import { environment as appEnvironment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  loading = false;
  isReady = false;
  isProd = false;
  appDisabled = false;
  updateAvailable = false;
  environmentLoaded = false;

  private environmentLoadedSubscription?: Subscription;

  constructor(
    protected _translate: TranslateService,
    protected _translationService: TranslatorService,
    protected platform: Platform,
    protected environment: EnvironmentConfigService,
    public practitonerService: PractitionerService,
    private sessionService: SessionService,
    private renderer: Renderer2,
    private _treatmentService: TreatmentService,
    private _offlineSyncService: OfflineSyncService,
    private _ngZone: NgZone,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private _safeAreaService: SafeAreaService,
    private _appVersionGuard: AppVersionGuardService,
    private authService: AuthService,
  ) {
    this.platform.ready().then(async (value: string) => {
      // Capture the browser URL before any navigation (needed to restore it on web refresh)
      const initialUrl = window.location.pathname + window.location.search + window.location.hash;

      let loggedOutForVersionChange = false;
      try {
        await this._safeAreaService.init();
        loggedOutForVersionChange = await this.initializeApp(value);
      } catch (err) {
        console.error('App initialization failed', err);
      }

      this.isReady = true;

      // Hard redirect from logout is pending — skip sync registration and routing.
      if (loggedOutForVersionChange) {
        return;
      }

      const encounterSyncTask = new SyncTask(
        StoragePrefixType.UnsyncedEncounter,
        30000,
        async (item: EncounterCompleteEncounterPost$Params, signal?: AbortSignal) => {
          // Replay cached body as-is (including Wave 2 practitionerId); do not rewrite attribution.
          return await this._treatmentService.syncEncounter(item, signal);
        },
        { ngZone: this._ngZone },
      );

      this._offlineSyncService.registerSyncTask(encounterSyncTask).subscribe((taskStatus) => {
        Events.encounterSyncStatusChanged.emit(taskStatus);
      });

      // On web: restore only a bearer that is still inside its TTL.
      // Closing the site stops the refresh timer, so a later reopen with an
      // expired access token must sign in again instead of opening Welcome.
      if (!Capacitor.isNativePlatform()) {
        if (await this.authService.accessTokenExpired()) {
          await this.authService.clearUserData(false);
          await this.router.navigateByUrl('/login', { replaceUrl: true });
          return;
        }

        this.sessionService.ensureIdleTimeoutInitialized();
        await this.authService.init();
        const publicPaths = [
          '/login',
          '/startup',
          '/forget-password',
          '/reset-password',
          '/landing',
        ];
        const isPublicPath = publicPaths.some(
          (p) =>
            initialUrl === p || initialUrl.startsWith(p + '/') || initialUrl.startsWith(p + '?'),
        );
        const restoreUrl =
          !isPublicPath && initialUrl && initialUrl !== '/' ? initialUrl : '/welcome';
        await this.router.navigateByUrl(restoreUrl, { replaceUrl: true });
        return;
      }

      await this.router.navigateByUrl('/startup', { replaceUrl: true });
    });
  }

  /**
   * @returns true when logout was initiated due to an app version change
   */
  async initializeApp(value: string): Promise<boolean> {
    await App.removeAllListeners();
    // Setup Environemt
    await this.environment.initService();

    this.isProd = this.environment.isProd;

    // Loading Service
    LoadingService.init();
    this.listenToLoading();

    // Setup Session Service
    this.sessionService.initializeSessionService();

    const loggedOutForVersionChange =
      await this._appVersionGuard.ensureVersionAndLogoutIfChanged();
    if (loggedOutForVersionChange) {
      return true;
    }

    // Translations
    await this._translationService.useLanguage(this._translationService.getCurrentLanguage());
    this._translate.onLangChange.subscribe(() => {});
    this.environmentLoadedSubscription = Events.environmentLoaded.subscribe(
      (config: ILocalAppEnvironment) => {
        this.environmentLoaded = true;
        this.applyVersionFlags(config);
      },
    );

    const existingConfig = this.environment.getCurrentConfig();
    if (existingConfig) {
      this.environmentLoaded = true;
      this.applyVersionFlags(existingConfig);
    }

    setTimeout(() => {
      const delayedConfig = this.environment.getCurrentConfig();
      if (delayedConfig) {
        this.environmentLoaded = true;
        this.applyVersionFlags(delayedConfig);
      }
    }, 1000);

    this.registerAppLinkHandlers();

    await App.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        IdleTimeoutService.handleAppResume();
        try {
          const loggedOut = await this._appVersionGuard.ensureVersionAndLogoutIfChanged();
          if (loggedOut) {
            return;
          }
          await this.environment.EnsureNetworkConfiguration();
          const config = this.environment.getCurrentConfig();
          if (config) {
            this.applyVersionFlags(config);
          }
        } catch {
          // Best-effort refresh on resume
        }
      }
    });

    // Screen-off often leaves the WebView "active" (no appStateChange). Re-check
    // wall-clock idle elapsed when the document becomes visible again.
    // Use window.document — ionicons also exports a `document` icon name in this file.
    window.document.addEventListener('visibilitychange', () => {
      if (window.document.visibilityState === 'visible') {
        IdleTimeoutService.handleAppResume();
      }
    });
    addIcons({
      add,
      close,
      arrowBack,
      bluetooth,
      chevronForwardOutline,
      textOutline,
      mailOutline,
      arrowUndoOutline,
      addCircleOutline,
      bodyOutline,
      refreshOutline,
      gridOutline,
      person,
      personOutline,
      text,
      code,
      keyOutline,
      arrowUpOutline,
      arrowBackOutline,
      arrowForwardOutline,
      documentOutline,
      addOutline,
      linkOutline,
      trashOutline,
      saveOutline,
      returnDownBackOutline,
      accessibilityOutline,
      checkmarkOutline,
      pencilOutline,
      unlinkSharp,
      eyeOffOutline,
      arrowDownOutline,
      cogOutline,
      checkmarkCircleOutline,
      eyeOutline,
      alertCircleOutline,
      warningOutline,
      flashOutline,
      closeCircleOutline,
      personAddOutline,
      cameraOutline,
      chevronBack,
      chevronDown,
      chevronForward,
      chevronUp,
      calendarNumberOutline,
      reorderThreeOutline,
      copyOutline,
      star,
      peopleOutline,
      logOutOutline,
      colorWand,
      document,
      library,
      documents,
      expand,
      contract,
      hammerOutline,
      homeOutline,
      settingsOutline,
      appsOutline,
      globeOutline,
      personCircleOutline,
      notificationsOutline,
    });

    return false;
  }

  listenToLoading(): void {
    LoadingService.loadingSub.pipe(delay(0)).subscribe((loading: any) => {
      this.loading = loading;
    });
  }

  private registerAppLinkHandlers(): void {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    const navigateFromAppLink = (urlString: string | undefined): void => {
      if (!urlString || !this.isAppOpenDeepLink(urlString)) {
        return;
      }
      void this.router.navigateByUrl('/startup', { replaceUrl: true });
    };

    void App.getLaunchUrl().then((result) => navigateFromAppLink(result?.url));

    void App.addListener('appUrlOpen', (event) => {
      this._ngZone.run(() => navigateFromAppLink(event.url));
    });
  }

  private isAppOpenDeepLink(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      const path = url.pathname.replace(/\/+$/, '') || '/';
      if (path !== '/app/open') {
        return false;
      }
      const host = url.hostname.toLowerCase();
      const allowedHosts = [
        `${appEnvironment.productionBaseUrl}.com`,
        `${appEnvironment.stagingBaseUrl}.com`,
      ];
      return allowedHosts.includes(host);
    } catch {
      return false;
    }
  }

  private applyVersionFlags(config: ILocalAppEnvironment | null): void {
    if (!config) {
      this.appDisabled = false;
      this.updateAvailable = false;
      return;
    }

    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'web') {
      this.appDisabled = false;
      this.updateAvailable = false;
      return;
    }

    this.appDisabled = config.versionDisabled === true;
    this.updateAvailable = !this.appDisabled && config.updateRequired === true;
  }

  async updateApp(): Promise<void> {
    await this.environment.OpenAppStore();
  }

  ngOnDestroy(): void {
    this.environmentLoadedSubscription?.unsubscribe();
  }
}
